import _ from 'lodash';
import qs from 'query-string';
import { batch } from 'react-redux';

import { addFormHasFailedEndpoints, handleErrors } from './ErrorActions';
import {
  fetchOfflineEntities,
  fetchOfflineSearchResults,
  setUnsynchronizedEntityId,
} from './OfflineDataActions';
import { setPanelLayoutConfig, unlockPanelLayoutConfig } from './PanelLayoutActions';
import { getTaskStatus } from './TaskStatusPanelActions';
import { getEntityData as getEntityDataApi } from '../api/entity-data';
import { DATA_CORRECTION_FORM_MODEL } from '../constants/DataCorrectionFormModel';
import { PANEL_CONFIGURATIONS } from '../constants/PanelLayoutConstants';
import * as Urls from '../constants/Urls';
import {
  createEntity,
  createFullEntity,
  createFullEntityOffline,
  loadData,
} from '../form-designer/actions/form-manager.actions';
import { handleFormChangesMode } from '../form-designer/actions/runtime.actions';
import {
  FORM_KEYS,
  SYSTEM_TABLE_KEYS_TO_EXCLUDE,
} from '../form-designer/constants/form-manager.constants';
import {
  DATA_CORRECTIONS_FORM_KEY,
  LINK_TARGETS,
} from '../form-designer/constants/runtime.constants';
import { setFetchErrorProp } from '../form-designer/utils/actions/form-manager.actions.utils';
import { isNilOrEmpty } from '../utils/ramdaUtils';

export const OPEN_ATTACHMENT_MODAL = 'OPEN_ATTACHMENT_MODAL';
export const CLOSE_ATTACHMENT_MODAL = 'CLOSE_ATTACHMENT_MODAL';
export const SET_OPEN_ERRORS_TAB_ENTITY_DISPLAY = 'SET_OPEN_ERRORS_TAB_ENTITY_DISPLAY';
export const SET_OPEN_STEP_FORM_TAB_ENTITY_DISPLAY = 'SET_OPEN_STEP_FORM_TAB_ENTITY_DISPLAY';
export const SET_OPEN_DETAIL_FORM_TAB_ENTITY_DISPLAY = 'SET_OPEN_DETAIL_FORM_TAB_ENTITY_DISPLAY';
export const CLEAR_ENTITY_DISPLAY = 'CLEAR_ENTITY_DISPLAY';
export const SET_ATTACHMENT_PREVIEW = 'SET_ATTACHMENT_PREVIEW';
export const POPUP_WINDOW_UPDATED = 'POPUP_WINDOW_UPDATED';

export const updatePopupWindowDialog = (changes) => ({
  type: POPUP_WINDOW_UPDATED,
  payload: changes,
});

export const openPopupWindowDialog = (url) => updatePopupWindowDialog({ visible: true, url });

export const closePopupWindowDialog = () => updatePopupWindowDialog({ visible: false, url: '' });

export const createEntityOrSetEntityForSynchronization = ({
  itmId,
  entityType,
  formKey,
  stepId,
  workflowId,
  containerWorkflowKey,
  workflowKey,
  parentWorkflowId,
  actionName,
  entityName,
  isDetailOnly = false,
  isStepForm = false,
}) => async (dispatch, getState) => {
  const { networkStatus } = getState();
  const { isOnline } = networkStatus;
  dispatch(fetchOfflineSearchResults());
  const entities = await dispatch(fetchOfflineEntities());
  const entityId = `${itmId}_${stepId}`;
  const savedEntityData = _.find(entities ?? [], { id: entityId });
  const clientChanges = savedEntityData?.requestChanges;
  const hasUnsynchedChanges = !isNilOrEmpty(clientChanges);
  const savedEntityExists = !isNilOrEmpty(savedEntityData);

  const shouldCreateFullEntityOffline = !isOnline && savedEntityExists && !isDetailOnly;

  if (shouldCreateFullEntityOffline) {
    dispatch(createFullEntityOffline(savedEntityData));
    return;
  }

  const shouldSetUnsynchronizedEntity = hasUnsynchedChanges && savedEntityExists && !isDetailOnly;

  if (shouldSetUnsynchronizedEntity) {
    dispatch(setUnsynchronizedEntityId(entityId));
    return;
  }

  if (isStepForm) {
    dispatch(
      createEntity({
        entityViewName: '',
        entityType,
        orgId: '',
        containerWorkflowKey,
        workflowKey,
        workflowId,
        stepId,
        storeKey: FORM_KEYS.STEP,
        itmId,
        entityName,
        parentWorkflowId,
        entityTypeForDisplay: entityName,
        actionName,
      })
    );

    return;
  }

  if (isDetailOnly) {
    dispatch(
      createEntity({
        entityViewName: '',
        entityType,
        orgId: '',
        formKey,
        containerWorkflowKey,
        workflowKey,
        workflowId: null,
        stepId: null,
        storeKey: FORM_KEYS.DETAIL,
        itmId,
        entityName: '',
        parentWorkflowId,
      })
    );
  } else {
    dispatch(
      createFullEntity({
        entityViewName: '',
        entityType,
        orgId: '',
        formKey,
        containerWorkflowKey,
        workflowKey,
        workflowId,
        stepId,
        itmId,
        entityName: '',
        parentWorkflowId,
      })
    );
  }
};

export function displayEntity(
  itmId,
  entityType,
  formKey,
  stepId,
  workflowId,
  containerWorkflowKey,
  workflowKey,
  parentWorkflowId,
  isDetailOnly
) {
  return async (dispatch, getState) => {
    const { auth } = getState();
    const { isArchiveMode } = auth;

    if (!isArchiveMode) {
      await dispatch(getTaskStatus(itmId, entityType));
    }

    const taskDisabled = getState().taskStatusPanel.disabled;

    const panelConfig =
      (isDetailOnly && taskDisabled) || isArchiveMode
        ? PANEL_CONFIGURATIONS.ONLY_RIGHT
        : isDetailOnly && !taskDisabled
        ? PANEL_CONFIGURATIONS.MIDDLE_COLLAPSED
        : !isDetailOnly && taskDisabled
        ? PANEL_CONFIGURATIONS.ONLY_MIDDLE
        : PANEL_CONFIGURATIONS.RIGHT_COLLAPSED;

    if (!isDetailOnly) {
      dispatch(unlockPanelLayoutConfig());
    }

    dispatch(setPanelLayoutConfig(panelConfig));
    dispatch(
      createEntityOrSetEntityForSynchronization({
        itmId,
        entityType,
        formKey,
        stepId,
        workflowId,
        containerWorkflowKey,
        workflowKey,
        parentWorkflowId,
        isDetailOnly,
      })
    );
  };
}

export const displayDataCorrectionForm = ({
  containerWorkflowKey,
  workflowKey,
  stepId,
  itmId,
  workflowId,
  parentWorkflowId,
  actionName,
  entityType,
}) => async (dispatch, getState) => {
  const state = getState();
  const { entityDefinition } = state.formDesigner[FORM_KEYS.DETAIL];
  const {
    panelLayout: { config, configLocked },
  } = state;

  const definitionKey = Object.keys(entityDefinition)[0];
  const detailFormEntityType = _.get(
    entityDefinition,
    [definitionKey, 'properties', 'TableName'],
    definitionKey
  );

  batch(() => {
    if (configLocked) {
      dispatch(unlockPanelLayoutConfig());
    }

    if (config === PANEL_CONFIGURATIONS.MIDDLE_COLLAPSED) {
      dispatch(setPanelLayoutConfig(PANEL_CONFIGURATIONS.ALL_EXPANDED));
    } else if (config === PANEL_CONFIGURATIONS.ONLY_RIGHT) {
      dispatch(setPanelLayoutConfig(PANEL_CONFIGURATIONS.LEFT_COLLAPSED));
    }
  });

  const entityDataResponse = await getEntityDataApi(
    entityType,
    itmId,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined
  );

  if (entityDataResponse.hasError) {
    const responseErrors = setFetchErrorProp(entityDataResponse.errors);

    batch(() => {
      dispatch(handleErrors(responseErrors));
      dispatch(addFormHasFailedEndpoints({ storeKey: FORM_KEYS.STEP }));
    });
    return;
  }

  const entityData = entityDataResponse.entityDataStore.entityData || {};

  //filter out System tables, since user is not allowed to edit them
  const newEntityData = {
    [detailFormEntityType]: Object.entries(entityData[detailFormEntityType]).reduce(
      (acc, [key, value]) => {
        if (Array.isArray(value) && SYSTEM_TABLE_KEYS_TO_EXCLUDE.some((k) => key.includes(k))) {
          return acc;
        }

        return { ...acc, [key]: value };
      },
      {}
    ),
  };

  const entityDataObj = {
    entityDataStore: { entityData: newEntityData },
    formInfo: { requiredESignature: 'true' },
  };

  const workflowInfo = {
    formKey: FORM_KEYS.STEP,
    containerWorkflowKey,
    workflowKey,
    workflowId,
    stepId,
    itmId,
    parentWorkflowId: parentWorkflowId ?? '',
    actionName: actionName ?? '',
  };

  const dataCorrectionFormObj = {
    workflowInfo,
    data: entityDataObj,
    originalData: entityDataObj,
    entityDefinition,
    formModel: DATA_CORRECTION_FORM_MODEL,
    formInfo: {
      formKey: DATA_CORRECTIONS_FORM_KEY,
    },
  };

  dispatch(loadData(FORM_KEYS.STEP, dataCorrectionFormObj));
};

export function displayStepForm({
  containerWorkflowKey,
  workflowKey,
  stepId,
  itmId,
  workflowId,
  parentWorkflowId,
  actionName,
}) {
  return async (dispatch, getState) => {
    const {
      panelLayout: { config, configLocked },
    } = getState();
    const { formInfo, entityDefinition } = getState().formDesigner[FORM_KEYS.DETAIL];

    const entityName = formInfo.formEntityKey;
    const definitionKey = Object.keys(entityDefinition)[0];
    const entityType = _.get(
      entityDefinition,
      [definitionKey, 'properties', 'TableName'],
      definitionKey
    );

    batch(() => {
      if (configLocked) {
        dispatch(unlockPanelLayoutConfig());
      }

      if (config === PANEL_CONFIGURATIONS.MIDDLE_COLLAPSED) {
        dispatch(setPanelLayoutConfig(PANEL_CONFIGURATIONS.ALL_EXPANDED));
      } else if (config === PANEL_CONFIGURATIONS.ONLY_RIGHT) {
        dispatch(setPanelLayoutConfig(PANEL_CONFIGURATIONS.LEFT_COLLAPSED));
      }
    });

    dispatch(
      createEntityOrSetEntityForSynchronization({
        itmId,
        entityType,
        stepId,
        workflowId,
        containerWorkflowKey,
        workflowKey,
        parentWorkflowId,
        isStepForm: true,
        entityName,
        actionName,
      })
    );
  };
}

export function openWorkflowStepLink(queryString) {
  return (dispatch) => {
    const params = qs.parse(queryString) || {};
    if (params.target === LINK_TARGETS.POPUP_WINDOW) {
      return dispatch(openPopupWindowDialog(`${Urls.WORKFLOW_STEP}?${queryString}&navs=0`));
    }

    return dispatch(
      displayStepForm({
        containerWorkflowKey: params.containerWorkflowKey,
        workflowKey: params.workflowKey,
        stepId: params.stepId,
        itmId: params.itmId,
        actionName: params.actionName,
        workflowId: params.workflowId,
        parentWorkflowId: params.parentWorkflowId,
      })
    );
  };
}

export const setAttachmentPreview = ({ filename, params, isOpen }) => ({
  type: SET_ATTACHMENT_PREVIEW,
  payload: { filename, params, isOpen },
});

export const openAttachmentModal = () => ({
  type: OPEN_ATTACHMENT_MODAL,
});

export const closeAttachmentModal = () => ({
  type: CLOSE_ATTACHMENT_MODAL,
});

export const setOpenErrorsTabEntityDisplay = (openErrorsTab) => ({
  type: SET_OPEN_ERRORS_TAB_ENTITY_DISPLAY,
  payload: { openErrorsTab },
});

export const setOpenStepFormTabEntityDisplay = (openStepFormTab) => ({
  type: SET_OPEN_STEP_FORM_TAB_ENTITY_DISPLAY,
  payload: { openStepFormTab },
});

export const setOpenDetailFormTabEntityDisplay = (openDetailFormTab) => ({
  type: SET_OPEN_DETAIL_FORM_TAB_ENTITY_DISPLAY,
  payload: { openDetailFormTab },
});

export const clearEntityDisplay = () => ({
  type: CLEAR_ENTITY_DISPLAY,
});

export const setIsFormChangesModeEntityDisplay = (flag, formKey) => (dispatch, getState) => {
  const {
    formDesigner,
    panelLayout: { config },
  } = getState();

  const isStepFormChangesMode = formDesigner[FORM_KEYS.STEP]?.isFormChangesMode;
  const isDetailFormChangesMode = formDesigner[FORM_KEYS.DETAIL]?.isFormChangesMode;

  batch(() => {
    if (formKey === FORM_KEYS.STEP && flag) {
      if (config === PANEL_CONFIGURATIONS.MIDDLE_COLLAPSED) {
        dispatch(setPanelLayoutConfig(PANEL_CONFIGURATIONS.ALL_EXPANDED));
      }

      if (config === PANEL_CONFIGURATIONS.ONLY_RIGHT) {
        dispatch(setPanelLayoutConfig(PANEL_CONFIGURATIONS.LEFT_COLLAPSED));
      }

      dispatch(handleFormChangesMode(true, FORM_KEYS.STEP));
      isDetailFormChangesMode && dispatch(handleFormChangesMode(false, FORM_KEYS.DETAIL));

      return;
    }

    if (formKey === FORM_KEYS.DETAIL && flag) {
      if (config === PANEL_CONFIGURATIONS.RIGHT_COLLAPSED) {
        dispatch(setPanelLayoutConfig(PANEL_CONFIGURATIONS.ALL_EXPANDED));
      }

      if (config === PANEL_CONFIGURATIONS.ONLY_MIDDLE) {
        dispatch(setPanelLayoutConfig(PANEL_CONFIGURATIONS.LEFT_COLLAPSED));
      }

      dispatch(handleFormChangesMode(true, FORM_KEYS.DETAIL));
      isStepFormChangesMode && dispatch(handleFormChangesMode(false, FORM_KEYS.STEP));

      return;
    }

    dispatch(handleFormChangesMode(flag, formKey));
  });
};
